import { useEffect, useState, type RefObject } from "react";
import {
  useWebsiteGetAppHref,
  WebsiteButtonLink,
} from "@-/dev.improve.haniverse.components.website";
import { LANDING_DOWNLOAD_SECTION_ID } from "#landing-download-section-id";

/**
 * Phone-width download bar that slides in once the hero's store badges have scrolled away, so a
 * download is always one tap from anywhere on the page. Hidden on desktop, where the hero and the
 * download section carry the QR code instead.
 */
export function LandingStickyDownloadBar({
  heroCallToActionRef,
}: {
  heroCallToActionRef: RefObject<HTMLElement | null>;
}) {
  const getAppHref = useWebsiteGetAppHref(`#${LANDING_DOWNLOAD_SECTION_ID}`);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const target = heroCallToActionRef.current;
    if (target === null) {
      return;
    }
    // The landing scrolls inside its own container (the document body is fixed), and an
    // IntersectionObserver against the viewport still sees that container's clipping.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry !== undefined) {
        setIsVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }
    });
    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [heroCallToActionRef]);

  return (
    <div
      aria-hidden={!isVisible}
      className={`fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 transition-[transform,opacity] duration-300 ease-out sm:hidden ${
        isVisible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-[130%] opacity-0"
      }`}
    >
      <div className="flex items-center gap-3 rounded-xs bg-website-ink/95 p-2.5 pr-2.5 text-white shadow-2xl ring-1 shadow-black/40 ring-white/10 backdrop-blur">
        <img
          alt=""
          className="size-10 rounded-xs ring-1 ring-white/15"
          height={80}
          src="/landing/haniverse-app-icon.png"
          width={80}
        />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold">Haniverse</div>
          <div className="truncate text-xs text-white/55">Free · iPhone &amp; Android</div>
        </div>
        <WebsiteButtonLink href={getAppHref} tabIndex={isVisible ? 0 : -1} variant="accent">
          Get the app
        </WebsiteButtonLink>
      </div>
    </div>
  );
}
